'use client'

import { Fragment, useEffect, useState } from 'react'
import Script from 'next/script'

type TrackingType = 'google-tag' | 'google-tag-manager' | 'whatconverts'

interface TrackingEntry {
	entry: string
	id: string
	type: TrackingType
}

interface SeoEntry {
	entry?: string
	id?: number | string
	type?: string
}

const trackingTypes = new Set<TrackingType>([
	'google-tag',
	'google-tag-manager',
	'whatconverts',
])

const getTrackingEntries = (seoData: unknown): TrackingEntry[] => {
	if (!Array.isArray(seoData)) return []

	const entries: TrackingEntry[] = []
	const seenEntries = new Set<string>()

	seoData.forEach(seoItem => {
		const seoEntries = seoItem?.seo_collection_id?.entries
		if (!Array.isArray(seoEntries)) return

		seoEntries.forEach((entryWrapper: any) => {
			const entry = entryWrapper?.seo_entries_id as SeoEntry | undefined
			const type = entry?.type as TrackingType | undefined
			const value = entry?.entry?.trim()

			if (!type || !trackingTypes.has(type) || !value) return

			const id = String(entry?.id ?? `${type}-${value}`)
			const deduplicationKey = `${type}:${id}:${value}`
			if (seenEntries.has(deduplicationKey)) return

			seenEntries.add(deduplicationKey)
			entries.push({ entry: value, id, type })
		})
	})

	return entries
}

const normalizeHttpsUrl = (value: string): string | null => {
	try {
		const url = new URL(value.startsWith('//') ? `https:${value}` : value)
		return url.protocol === 'https:' ? url.toString() : null
	} catch {
		return null
	}
}

export default function TrackingScripts({ seoData }: { seoData?: unknown }) {
	const [isHydrated, setIsHydrated] = useState(false)

	useEffect(() => {
		setIsHydrated(true)
	}, [])

	// The server and the first client render must match exactly. Tracking starts
	// only after React has committed the page hydration.
	if (!isHydrated) return null

	const trackingEntries = getTrackingEntries(seoData)
	const isProduction =
		process.env.NODE_ENV === 'production' &&
		process.env.ENVIRONMENT !== 'development'

	return (
		<>
			{trackingEntries.map(entry => {
				if (entry.type === 'google-tag-manager') {
					return (
						<Script
							key={`gtm-${entry.id}`}
							id={`seo-gtm-${entry.id}`}
							strategy='afterInteractive'
						>
							{`
								(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
								new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
								j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
								'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
								})(window,document,'script','dataLayer',${JSON.stringify(entry.entry)});
							`}
						</Script>
					)
				}

				if (entry.type === 'google-tag') {
					return (
						<Fragment key={`google-tag-${entry.id}`}>
							<Script
								id={`seo-google-tag-source-${entry.id}`}
								src={`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(entry.entry)}`}
								strategy='afterInteractive'
							/>
							<Script
								id={`seo-google-tag-config-${entry.id}`}
								strategy='afterInteractive'
							>
								{`
									window.dataLayer = window.dataLayer || [];
									function gtag(){dataLayer.push(arguments);}
									gtag('js', new Date());
									gtag('config', ${JSON.stringify(entry.entry)});
								`}
							</Script>
						</Fragment>
					)
				}

				if (entry.type === 'whatconverts' && isProduction) {
					const sourceUrl = normalizeHttpsUrl(entry.entry)
					if (!sourceUrl) return null

					return (
						<Script
							key={`whatconverts-${entry.id}`}
							id={`seo-whatconverts-${entry.id}`}
							strategy='lazyOnload'
						>
							{`
								window.$wc_load = function(a) {
									return JSON.parse(JSON.stringify(a));
								};
								window.$wc_leads = window.$wc_leads || {
									doc: {
										url: window.$wc_load(document.URL),
										ref: window.$wc_load(document.referrer),
										search: window.$wc_load(location.search),
										hash: window.$wc_load(location.hash)
									}
								};
								var whatConvertsScript = document.createElement('script');
								whatConvertsScript.async = true;
								whatConvertsScript.src = ${JSON.stringify(sourceUrl)};
								(document.head || document.body).appendChild(whatConvertsScript);
							`}
						</Script>
					)
				}

				return null
			})}
		</>
	)
}
